import { BlobClient } from '@azure/storage-blob';
import api from 'helpers/api';

import { API } from '@thuas/pd-schemas';
import { sanitizeFileName } from 'helpers/export';
import { prepareForBackend } from 'helpers/objects';

export type IFile = API['File']['read'];
export type IFileCreate = Omit<
  API['File']['create'],
  | 'file_path'
  | 'fileName'
  | 'uri'
  | 'avatarsFor'
  | 'sectionFiles'
  | 'projectImageFor'
  | 'publicationImageFor'
  | 'memberImageFor'
> & {
  sectionFiles?: [id: number];
  sectionFilesAdd?: { id: number }[];
};

type IFileUpdate = API['File']['update'] & {
  sectionFilesOverwrite?: { id: number }[];
};
function prepareForUpdate(file: IFile): IFileUpdate {
  const updated = prepareForBackend(file, [
    'id',
    'author',
    'uri',
    'fileName',
    'file_path',
    'uploadTime',
  ]) as IFileUpdate;
  return updated;
}

export async function postFile(file: File, data: IFileCreate) {
  try {
    const fileName = sanitizeFileName({ input: file.name });
    const response = await api.post('File', {
      ...data,
      fileName: fileName,
      sectionFilesAdd: data.sectionFiles
        ? data.sectionFiles.map((x) => ({ id: x }))
        : [],
    });
    const blobClient = new BlobClient(response.data.message);
    await blobClient.getBlockBlobClient().uploadData(file, {
      blobHTTPHeaders: {
        blobContentType: file.type || 'application/octet-stream',
        blobContentDisposition: `inline; filename="${fileName}"`,
      },
    });
    return response;
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
}

export const getFile = async (id: number) => {
  try {
    const response = await api.get(`File/${id}?and=*`);
    return response;
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
};

export const getFileURIByFilePath = async (file_path: string) => {
  try {
    const response = await api.get(`File?and=*`);
    for (const file of response.data) {
      if (file.file_path === file_path) {
        return file.uri;
      }
    }
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
};

export const patchFile = async (data: IFile) => {
  try {
    const response = await api.patch('File/' + data.id, prepareForUpdate(data));
    return response;
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
};

// TODO: can we take of removing the blob in the backend
export async function deleteFile(fileId?: number) {
  try {
    if (!fileId) return;
    const findFile = await api.get(`File/${fileId}?and=uri`);
    const response = await api.delete(`File/${fileId}`);

    if (response.status === 204) {
      const blobClient = new BlobClient(findFile.data.uri);
      await blobClient.getBlockBlobClient().deleteIfExists();
    }

    return response;
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
}

export async function deleteLargeText(id?: number) {
  try {
    if (!id) return;
    const response = await api.delete('LargeText/' + id);
    return response;
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
}
